import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getFeatured, TYPE_LABEL } from "@/lib/media";

const slides = getFeatured();

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const go = (direction: number) => {
    setIndex((prev) => (prev + direction + slides.length) % slides.length);
  };

  if (slides.length === 0) return null;

  return (
    <section
      aria-label="焦点内容推荐"
      className="relative h-[460px] w-full overflow-hidden bg-primary-950 select-none md:h-[600px]"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
        touchStartX.current = null;
      }}
    >
      {slides.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照背景`}
            title={`${item.title} 中文字幕`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/30"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
              <div className="max-w-xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-primary-950">
                    最新推荐
                  </span>
                  <span className="rounded-full border border-background-50/40 px-3 py-1 text-xs text-background-100">
                    {TYPE_LABEL[item.type]} · {item.year}
                  </span>
                </div>
                <h2 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                  {item.title}
                </h2>
                <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-background-200 md:text-sm">
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {item.region}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-movie-2-line"></i>
                    {item.genres.slice(0, 3).join(" / ")}
                  </span>
                  <span className="flex items-center gap-1 text-accent-300">
                    <i className="ri-star-fill"></i>
                    {item.rating}
                  </span>
                </p>
                <p className="mt-4 line-clamp-3 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
                  {item.summary}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${item.id}`}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    查看详情
                  </Link>
                  <Link
                    to={`/category/${item.type}`}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-3 text-sm text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-closed-captioning-line"></i>
                    同类型字幕
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-xl text-background-50 transition-colors hover:bg-foreground-950/60 md:flex"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-xl text-background-50 transition-colors hover:bg-foreground-950/60 md:flex"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/60 hover:bg-background-50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}